import { useCallback, useEffect, useState } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useCachedState } from "@/lib/swr";
import { useDash } from "../ctx";
import { ArrowLeft } from "lucide-react";
import { PulseDot, PulseWord } from "@/components/pulse/PulseDot";
import { SummaryPanel } from "@/components/pulse/SummaryPanel";
import { PulseMath } from "@/components/pulse/PulseMath";
import { PulseActions } from "@/components/pulse/PulseActions";
import { PulseDrill } from "@/components/pulse/PulseDrill";
import { BAND, bandOf, movementLine, fmtTime, fmtDay } from "@/components/pulse/pulse";

function History({ history, thresholds }) {
  const rows = (history || []).filter((h) => typeof h.score === "number");
  if (rows.length < 2) return null;
  return (
    <div className="rounded-2xl border border-[#E8E2D9] bg-white px-5 py-4" data-testid="pulse-history">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] font-extrabold uppercase tracking-[0.08em] text-[#8C827A]">Last {rows.length} days</span>
        <span className="text-[11px] text-[#8C827A]">{fmtDay(rows[0].day)} → {fmtDay(rows[rows.length - 1].day)}</span>
      </div>
      <div className="flex items-end gap-[3px] h-[72px]">
        {rows.map((h) => (
          <div key={h.day} title={`${fmtDay(h.day)} · ${h.score}`} data-testid={`pulse-history-bar-${h.day}`}
            className="flex-1 rounded-t-sm min-w-[4px]" style={{ height: `${Math.max(6, h.score)}%`, backgroundColor: (BAND[h.band] || BAND.quiet).dot }} />
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-[#A8A29E] mt-1.5"><span>Red below {thresholds?.red_below ?? 60}</span><span>Green from {thresholds?.green_from ?? 80}</span></div>
    </div>
  );
}

// Staying engaged is shown for context, never scored (STOP texts are too rare and too unrelated to service).
function EngagementNote({ info, tenantId, name }) {
  const [drill, setDrill] = useState(null);
  const share = typeof info.share === "number" ? ` · ${Math.round(info.share)}% of the roster used the concierge` : "";
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-dashed border-[#E8E2D9] bg-white px-5 py-3 text-[13px] text-[#57534E]" data-testid="pulse-engagement-note">
      <span><b className="text-[#1C1917]">Staying engaged</b> <span className="text-[#8C827A]">(for context, not scored)</span> · {info.optouts || 0} texted STOP this month of {info.optins || 0} opted in{share}</span>
      <button type="button" data-testid="pulse-engagement-see" onClick={() => setDrill("engagement")} className="text-xs font-bold text-[#1C1917] hover:underline">See who →</button>
      <PulseDrill tenantId={tenantId} signal={drill} name={name} onClose={() => setDrill(null)} />
    </div>
  );
}

// One community's Pulse, full page (sidebar → Pulse). Own community only. Company / Redline roles get a
// way back to the whole portfolio's list from here.
export default function CommunityPulse({ embedded = false }) {
  const { tenantId, tenant, role, actualRole, viewAsUser, exitCommunity, viewCompany } = useDash();
  const [data, setData] = useCachedState(`pulse-card:${tenantId}`, null);
  const [loading, setLoading] = useState(!data);
  const load = useCallback(() => api.get(`/tenants/${tenantId}/community-pulse`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false)), [tenantId, setData]);
  useEffect(() => { load(); }, [load]);
  const portfolioRole = !viewAsUser && ["company_admin", "regional_manager", "super_admin", "platform_admin"].includes(actualRole || role);
  const showBack = portfolioRole && (viewCompany || ["company_admin", "regional_manager"].includes(actualRole || role)) && data?.company_id;
  const row = data?.on ? { pulse_on: true, snapshot: data.snapshot, name: data.name } : { pulse_on: false };
  const band = bandOf(row);
  const s = data?.snapshot;
  const scored = data?.on && band !== "quiet";

  return (
    <div className={embedded ? "space-y-5" : "max-w-[1000px] space-y-5"} data-testid="community-pulse">
      {!embedded && (
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end sm:justify-between gap-3 sm:gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#8A8378] mb-1.5">{tenant?.name || "Community"}</div>
          <PageTitle title="Pulse" about={<>How this community is doing, from what residents actually said and did. Updated each morning{s?.computed_at ? ` · ${fmtTime(s.computed_at)}` : ""}.</>} />
        </div>
        {showBack && (
          <button type="button" data-testid="pulse-back-to-portfolio" onClick={() => exitCommunity("/dashboard/portfolio-pulse")}
            className="self-start inline-flex items-center gap-1.5 rounded-full border border-[#E8E2D9] bg-white px-3.5 py-1.5 text-[13px] font-bold text-[#1C1917] hover:border-ink">
            <ArrowLeft className="h-4 w-4" strokeWidth={2} /> All communities' Pulse
          </button>
        )}
      </div>
      )}

      {loading && !data ? (
        embedded ? null : <div className="flex justify-center py-24"><div className="h-8 w-8 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>
      ) : !data?.on ? (
        embedded ? null : <div className="rounded-2xl border border-[#E8E2D9] bg-white p-10 text-center text-sm text-[#78716C]" data-testid="pulse-off">Pulse isn't on for this community. Turn it on under Settings → Capabilities.</div>
      ) : (
        <>
          <section className="rounded-2xl border border-[#E8E2D9] bg-white overflow-hidden" data-testid="pulse-hero">
            <div className="grid grid-cols-[28px_1fr] sm:grid-cols-[32px_1fr_auto] gap-4 sm:gap-5 items-center px-4 sm:px-6 py-5">
              <PulseDot band={band} quiet={!!s?.expected} size={18} testid="pulse-hero-dot" />
              <div className="min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <PulseWord band={band} testid="pulse-hero-word" />
                  {scored && <span className="font-mono text-3xl font-black text-[#1C1917] leading-none" data-testid="pulse-hero-score">{s.score}</span>}
                  {s?.falling && <span className="text-[10px] font-extrabold text-[#B91C1C] uppercase tracking-wide">falling</span>}
                  {s?.expected && <span className="text-[10px] font-bold text-[#8C827A] uppercase tracking-wide" data-testid="pulse-hero-expected">· expected</span>}
                </div>
                <div className="text-sm font-bold text-[#8C827A] mt-1.5" data-testid="pulse-hero-move">{movementLine(row)}</div>
              </div>
            </div>
            {scored && (
              <div className="px-4 pb-4 -mt-1">
                <SummaryPanel tenantId={tenantId} name={data.name} snapshot={s} canExpected onExpectedChange={load} />
              </div>
            )}
            {!scored && <div className="px-6 pb-5 text-sm text-[#57534E]" data-testid="pulse-quiet-note">Too little resident activity this month to score fairly — the dot stays grey until at least 15 conversations or ratings land in a 30-day window.</div>}
          </section>
          <PulseActions tenantId={tenantId} name={data.name} rows={data.act} />
          {data.info && <EngagementNote info={data.info} tenantId={tenantId} name={data.name} />}
          <History history={data.history} thresholds={data.math?.thresholds} />
          <PulseMath math={data.math} name={data.name} tenantId={tenantId} />
        </>
      )}
    </div>
  );
}
